"use client"
// The cookie banner (vanilla-cookieconsent). Mounted once in the root layout. Rules, same as every client
// site: shown to every visitor (no geo guess); Accept all / Reject all / Choose with equal weight; nothing
// non-essential loads before a choice (see src/lib/consent.ts); withdrawing deletes the cookies and reloads;
// "Cookie settings" in the footer reopens it; every choice is logged to consent.boldpiq.com.
import { useEffect } from "react"
import * as CookieConsent from "vanilla-cookieconsent"
import "vanilla-cookieconsent/dist/cookieconsent.css"
import { CONSENT_LOG_URL, CONSENT_VERSION, googleConsentSignals } from "@/lib/consent"

function record() {
  const c = CookieConsent.getCookie()
  fetch(CONSENT_LOG_URL, {
    method: "POST",
    // text/plain keeps it a "simple" cross-origin request: no CORS preflight, so keepalive works
    // when the visitor leaves straight after clicking. The Worker parses the body as JSON either way.
    headers: { "content-type": "text/plain" },
    keepalive: true,
    body: JSON.stringify({ id: c.consentId, categories: c.categories, version: CONSENT_VERSION, page: location.pathname }),
  }).catch(() => {}) // the site works either way; the log's health is checked separately
}

const clear = (...names: string[]) => names.map((n) => ({ name: new RegExp("^" + n.replace("*", ".*") + "$") }))

export function ConsentBanner() {
  useEffect(() => {
    CookieConsent.run({
      revision: CONSENT_VERSION,
      cookie: { name: "cc_cookie", expiresAfterDays: 182, sameSite: "Lax" },
      categories: {
        necessary: { enabled: true, readOnly: true },
        functional: { autoClear: { cookies: [], reloadPage: true } },
        analytics: { autoClear: { cookies: clear("_ga", "_ga_*", "_clck", "_clsk", "CLID"), reloadPage: true } },
        marketing: { autoClear: { cookies: clear("_fbp"), reloadPage: true } },
      },
      guiOptions: {
        consentModal: { layout: "box", position: "bottom left", equalWeightButtons: true, flipButtons: false },
        preferencesModal: { layout: "box", equalWeightButtons: true, flipButtons: false },
      },
      onFirstConsent: record,
      onChange: () => {
        record()
        const w = window as unknown as { gtag?: (...a: unknown[]) => void; clarity?: (...a: unknown[]) => void }
        w.gtag?.("consent", "update", googleConsentSignals())
        // Analytics withdrawn: tell Clarity to stop now, so it sends nothing more while the page reloads.
        if (!CookieConsent.acceptedCategory("analytics")) w.clarity?.("consent", false)
      },
      language: {
        default: "en",
        translations: {
          en: {
            consentModal: {
              title: "Cookies on this site",
              description:
                'We’d like to use Google Analytics, Microsoft Clarity, the Meta Pixel and our live chat. Nothing loads until you choose. <a href="/cookie-policy">Cookie policy</a>',
              acceptAllBtn: "Accept all",
              acceptNecessaryBtn: "Reject all",
              showPreferencesBtn: "Choose",
            },
            preferencesModal: {
              title: "Cookie settings",
              acceptAllBtn: "Accept all",
              acceptNecessaryBtn: "Reject all",
              savePreferencesBtn: "Save my choice",
              closeIconLabel: "Close",
              sections: [
                { description: 'Pick what this site may use. You can change it any time from "Cookie settings" at the bottom of every page.' },
                {
                  title: "Strictly necessary",
                  description: "Remembers the choice you make here, protects our forms from bots (Cloudflare Turnstile), and runs the booking and contact forms when you open them. Always on.",
                  linkedCategory: "necessary",
                },
                { title: "Live chat", description: "Loads the chat window in the corner (GoHighLevel). Its provider sets cookies to keep your conversation open between pages.", linkedCategory: "functional" },
                { title: "Analytics", description: "Google Analytics and Microsoft Clarity count visits and show which pages help, so we can fix the ones that don’t.", linkedCategory: "analytics" },
                { title: "Advertising", description: "The Meta Pixel tells Meta (Facebook, Instagram) you visited, so our ads reach people who were here and we can see which ads work.", linkedCategory: "marketing" },
                { title: "More", description: 'Read the <a href="/cookie-policy">cookie policy</a> and the <a href="/privacy">privacy policy</a>.' },
              ],
            },
          },
        },
      },
    })
  }, [])
  return null
}

/** Footer link that reopens the banner's settings. */
export function CookieSettingsButton({ style, className }: { style?: React.CSSProperties; className?: string }) {
  return (
    <button type="button" className={className} style={{ background: "none", border: 0, padding: 0, cursor: "pointer", font: "inherit", ...style }} onClick={() => CookieConsent.showPreferences()}>
      Cookie settings
    </button>
  )
}
